import { useCallback, useEffect, useState } from 'react';

const THEME_KEY = 'simplificalo:theme';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const stored = window.localStorage.getItem(THEME_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  return 'dark';
}

function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(resolved);
}

export interface UseThemeResult {
  readonly theme: Theme;
  readonly resolved: ResolvedTheme;
  readonly setTheme: (next: Theme) => void;
  readonly cycleTheme: () => void;
}

export function useTheme(): UseThemeResult {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => {
    const t = readStoredTheme();
    return t === 'system' ? getSystemTheme() : t;
  });

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    window.localStorage.setItem(THEME_KEY, next);
    const r = next === 'system' ? getSystemTheme() : next;
    setResolved(r);
    applyTheme(r);
  }, []);

  // Cycle dark → light → system → dark.
  const cycleTheme = useCallback(() => {
    setThemeState((current) => {
      const next: Theme = current === 'dark' ? 'light' : current === 'light' ? 'system' : 'dark';
      window.localStorage.setItem(THEME_KEY, next);
      const r = next === 'system' ? getSystemTheme() : next;
      setResolved(r);
      applyTheme(r);
      return next;
    });
  }, []);

  // Cuando theme === 'system', escuchar cambios en prefers-color-scheme del OS.
  useEffect(() => {
    if (theme !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (): void => {
      const r = getSystemTheme();
      setResolved(r);
      applyTheme(r);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  return { theme, resolved, setTheme, cycleTheme };
}
